import { useTranslation } from 'react-i18next';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import LegalDocument from './LegalDocument';
import './LegalModal.css';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';

interface LegalModalProps {
  type: 'terms' | 'privacy';
  isOpen: boolean;
  onClose: () => void;
}

export default function LegalModal({ type, isOpen, onClose }: LegalModalProps) {
  const { t, i18n } = useTranslation();
  /*
   * Anche questo pannello si registra fra gli overlay.
   *
   * Non lo faceva, e nello stack c'era solo `AuthModal`: uno swipe dal bordo
   * sinistro — la gesture «indietro» di Safari iOS, che parte facilissima
   * mentre si scorre col pollice un testo legale lungo — chiudeva
   * **l'iscrizione** invece dei Termini. E `legalModalType` restava
   * valorizzato, quindi riaprendo «Registrati» il pannello dei Termini
   * ricompariva da solo sopra il modulo.
   *
   * Prima del `return null`, per le regole degli hook.
   */
  useOverlayBackButton(isOpen, onClose);
  const riferimentoModale = useModaleAccessibile<HTMLDivElement>(isOpen, onClose);

  if (!isOpen) return null;

  const title = type === 'terms' ? t('legal.termsTitle') : t('legal.privacyTitle');

  return (
    /*
     * ⚠️ `stopPropagation` sull'OVERLAY, non solo sul pannello.
     *
     * Questo modale viene renderizzato DENTRO l'overlay di `AuthModal` (gli e'
     * fratello, quindi suo discendente). Senza fermare qui la propagazione, un
     * tocco sullo sfondo scuro chiudeva questo pannello E risaliva all'overlay
     * dell'iscrizione, che chiama `handleClose` -> `resetForm()`: spunte, nome,
     * email e password **azzerati**, e l'iscrizione chiusa.
     *
     * Su telefono e' il gesto piu' naturale del mondo: l'iscrizione e' a tutto
     * schermo, quindi l'unico sfondo che l'utente vede e' questo, e «tocca
     * fuori per chiudere» e' il riflesso condizionato di iOS. La × invece era
     * al sicuro, perche' sta dentro il pannello.
     */
    <div
      className="legal-modal-overlay"
      onClick={(e) => { e.stopPropagation(); onClose(); }}
    >
      {/*
        * Dialogo dichiarato, e fuoco governato (`useModaleAccessibile`): senza,
        * chi apre i termini da tastiera continua a tabulare nel modulo di
        * registrazione SOTTO, che è ancora lì dietro il velo — e la finestra che
        * si è aperta, per uno screen reader, non esiste.
        */}
      <div
        ref={riferimentoModale}
        className="legal-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-modal-titolo"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="legal-modal-header">
          <h3 id="legal-modal-titolo">{title}</h3>
          <button className="legal-modal-close" onClick={onClose} type="button" aria-label={t('common.close')}>
            &times;
          </button>
        </div>
        <div className="legal-modal-body">
          <LegalDocument lang={i18n.language} kind={type} />
        </div>
      </div>
    </div>
  );
}
